class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.scope = this.closest('.search-modal__content') || this.closest('header') || this.parentElement || document;
    this.input = this.resolveInput();
    this.form = this.resolveForm();
    this.header = this.closest('header');
    this.predictiveSearchResults = this.resolveResultsContainer();
    this.statusElement = this.resolveStatusElement();
    this.isOpen = false;
    this.closeTimeout = null;

    this.onDocumentPointerDown = this.onDocumentPointerDown.bind(this);

    this.setupEventListeners();
  }

  disconnectedCallback() {
    document.removeEventListener('pointerdown', this.onDocumentPointerDown);
  }

  setupEventListeners() {
    if (!this.input || !this.predictiveSearchResults) return;

    // If another predictive-search element already owns this input/results, avoid double-binding.
    const inputOwner = this.input.closest('predictive-search');
    if (inputOwner && inputOwner !== this) return;

    const resultsOwner = this.predictiveSearchResults.closest('predictive-search');
    if (resultsOwner && resultsOwner !== this) return;

    if (this.form) {
      this.form.addEventListener('submit', this.onFormSubmit.bind(this));
    }

    document.addEventListener('pointerdown', this.onDocumentPointerDown);

    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 300).bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.input.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.getResultsRoot().querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFocus() {
    clearTimeout(this.closeTimeout);

    const searchTerm = this.getQuery();
    this.header?.classList.add('search-expanded');

    if (!searchTerm.length) return;

    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut(event) {
    const nextTarget = event.relatedTarget;
    if (nextTarget && this.isInsideExperience(nextTarget)) return;

    clearTimeout(this.closeTimeout);
    this.closeTimeout = setTimeout(() => {
      const activeElement = document.activeElement;
      if (activeElement && this.isInsideExperience(activeElement)) return;

      if (this.predictiveSearchResults && this.predictiveSearchResults.matches(':hover')) return;

      this.header?.classList.remove('search-expanded');
      this.close();
    }, 120);
  }

  onDocumentPointerDown(event) {
    if (this.isInsideExperience(event.target)) return;

    this.header?.classList.remove('search-expanded');
    this.close();
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const resultsRoot = this.getResultsRoot();
    const moveUp = direction === 'up';
    const selectedElement = resultsRoot.querySelector('[aria-selected="true"]');
    const allElements = resultsRoot.querySelectorAll('[role="option"]');
    let activeElement = resultsRoot.querySelector('[role="option"]');

    if (!allElements.length || (moveUp && !selectedElement)) return;

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.getResultsRoot().querySelector('[aria-selected="true"] a, [aria-selected="true"] button');

    if (selectedProduct) selectedProduct.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[type]')}=query,product,article,page&${encodeURIComponent('resources[limit]')}=10&section_id=predictive-search-new`)
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search-new').innerHTML;
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.resolveStatusElement();
    if (!this.statusElement) return;

    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;

    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');

    const liveRegionCountValue = this.getResultsRoot().querySelector('[data-predictive-search-live-region-count-value]');
    if (!liveRegionCountValue) return;

    this.setLiveRegionText(liveRegionCountValue.textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight = window.innerHeight - document.getElementsByClassName('section-header')[0].getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    if (!this.predictiveSearchResults || !this.input) return;

    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    clearTimeout(this.closeTimeout);

    if (!this.input || !this.predictiveSearchResults) return;

    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }

    const selected = this.getResultsRoot().querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false
    this.predictiveSearchResults.removeAttribute('style');

    this.isOpen = false;
  }

  resolveInput() {
    return this.querySelector('input[type="search"]')
      || this.scope.querySelector('input[type="search"][aria-controls="predictive-search-results-list"]')
      || this.scope.querySelector('input[type="search"][aria-owns="predictive-search-results-list"]')
      || this.scope.querySelector('input[type="search"]');
  }

  resolveForm() {
    return this.querySelector('form.search')
      || this.input?.closest('form.search')
      || this.scope.querySelector('form.search');
  }

  resolveResultsContainer() {
    return this.querySelector('[data-predictive-search]')
      || this.scope.querySelector('[data-predictive-search]');
  }

  resolveStatusElement() {
    return this.querySelector('.predictive-search-status')
      || this.scope.querySelector('.predictive-search-status');
  }

  getResultsRoot() {
    return this.predictiveSearchResults || this;
  }

  isInsideExperience(element) {
    if (!element) return false;

    return this.contains(element)
      || !!(this.input && this.input.contains(element))
      || !!(this.form && this.form.contains(element))
      || !!(this.predictiveSearchResults && this.predictiveSearchResults.contains(element));
  }
}

customElements.define('predictive-search', PredictiveSearch);
